/**
 * Utilitários para otimizações de performance e acessibilidade
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

interface BrowserNetworkInformation extends EventTarget {
  type?: string;
  effectiveType?: string;
  saveData?: boolean;
}

type NetworkNavigator = Navigator & {
  connection?: BrowserNetworkInformation;
  mozConnection?: BrowserNetworkInformation;
  webkitConnection?: BrowserNetworkInformation;
};

// === DEBOUNCE E THROTTLE ===

/**
 * Hook para debounce de valores
 */
export const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

/**
 * Hook para throttle de funções
 */
export const useThrottle = <TArgs extends unknown[]>(
  callback: (...args: TArgs) => unknown,
  delay: number
): ((...args: TArgs) => void) => {
  const throttling = useRef(false);

  return useCallback(
    (...args: TArgs) => {
      if (!throttling.current) {
        callback(...args);
        throttling.current = true;
        setTimeout(() => {
          throttling.current = false;
        }, delay);
      }
    },
    [callback, delay]
  );
};

// === LAZY LOADING ===

/**
 * Hook para lazy loading com Intersection Observer
 */
export const useLazyLoad = (
  threshold: number = 0.1,
  rootMargin: string = '50px'
) => {
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || isLoaded) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          setIsLoaded(true);
          observer.unobserve(element);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => {
      observer.unobserve(element);
    };
  }, [threshold, rootMargin, isLoaded]);

  return { elementRef, isInView, isLoaded };
};

// === VIRTUAL SCROLLING ===

/**
 * Hook para virtual scrolling de listas grandes
 */
export const useVirtualScroll = <T>(
  items: T[],
  itemHeight: number,
  containerHeight: number,
  overscan: number = 5
) => {
  const [scrollTop, setScrollTop] = useState(0);

  const visibleRange = useMemo(() => {
    const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
    const endIndex = Math.min(
      items.length - 1,
      Math.floor((scrollTop + containerHeight) / itemHeight) + overscan
    );

    return { startIndex, endIndex };
  }, [scrollTop, itemHeight, containerHeight, overscan, items.length]);

  const visibleItems = useMemo(() => {
    return items.slice(visibleRange.startIndex, visibleRange.endIndex + 1).map((item, index) => ({
      item,
      index: visibleRange.startIndex + index,
      offsetY: (visibleRange.startIndex + index) * itemHeight,
    }));
  }, [items, visibleRange, itemHeight]);

  const totalHeight = items.length * itemHeight;

  const handleScroll = useCallback((event: React.UIEvent<HTMLElement>) => {
    setScrollTop(event.currentTarget.scrollTop);
  }, []);

  return {
    visibleItems,
    totalHeight,
    handleScroll,
    containerProps: {
      style: { height: containerHeight, overflow: 'auto' },
      onScroll: handleScroll,
    },
  };
};

// === MEMORIZAÇÃO E CACHE ===

/**
 * Hook para cache com TTL (Time To Live)
 */
export const useCache = <T>(
  key: string,
  fetcher: () => Promise<T>,
  ttl: number = 300000 // 5 minutos
) => {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const cacheRef = useRef<Map<string, { data: T; timestamp: number }>>(new Map());

  const fetchData = useCallback(async () => {
    const cached = cacheRef.current.get(key);
    const now = Date.now();

    // Verificar se existe cache válido
    if (cached && now - cached.timestamp < ttl) {
      setData(cached.data);
      return cached.data;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await fetcher();
      cacheRef.current.set(key, { data: result, timestamp: now });
      setData(result);
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [key, fetcher, ttl]);

  const invalidateCache = useCallback(() => {
    cacheRef.current.delete(key);
  }, [key]);

  const clearAllCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refetch: fetchData,
    invalidateCache,
    clearAllCache,
  };
};

// === PERFORMANCE MONITORING ===

/**
 * Hook para monitorar performance de componentes
 */
export const usePerformanceMonitor = (componentName: string) => {
  const renderCount = useRef(0);
  const startTime = useRef<number>(0);

  useEffect(() => {
    renderCount.current += 1;
    startTime.current = performance.now();

    return () => {
      const endTime = performance.now();
      const renderTime = endTime - startTime.current;

      if (process.env.NODE_ENV === 'development') {
        console.log(`[Performance] ${componentName}:`, {
          renderCount: renderCount.current,
          renderTime: `${renderTime.toFixed(2)}ms`,
        });

        // Alertar sobre renders excessivos
        if (renderTime > 100) {
          console.warn(`[Performance Warning] ${componentName} took ${renderTime.toFixed(2)}ms to render`);
        }
      }
    };
  });

  return {
    renderCount: renderCount.current,
  };
};

// === WEB VITALS ===

/**
 * Hook para monitorar Web Vitals
 */
export const useWebVitals = () => {
  const [metrics, setMetrics] = useState<{
    CLS?: number;
    FID?: number;
    FCP?: number;
    LCP?: number;
    TTFB?: number;
  }>({});

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Importar web-vitals dinamicamente para evitar SSR issues
    import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
      getCLS((metric) => {
        setMetrics(prev => ({ ...prev, CLS: metric.value }));
      });

      getFID((metric) => {
        setMetrics(prev => ({ ...prev, FID: metric.value }));
      });

      getFCP((metric) => {
        setMetrics(prev => ({ ...prev, FCP: metric.value }));
      });

      getLCP((metric) => {
        setMetrics(prev => ({ ...prev, LCP: metric.value }));
      });

      getTTFB((metric) => {
        setMetrics(prev => ({ ...prev, TTFB: metric.value }));
      });
    });
  }, []);

  const getPerformanceGrade = () => {
    const { CLS, FID, LCP } = metrics;
    let score = 0;
    let total = 0;

    if (CLS !== undefined) {
      score += CLS < 0.1 ? 100 : CLS < 0.25 ? 50 : 0;
      total += 100;
    }

    if (FID !== undefined) {
      score += FID < 100 ? 100 : FID < 300 ? 50 : 0;
      total += 100;
    }

    if (LCP !== undefined) {
      score += LCP < 2500 ? 100 : LCP < 4000 ? 50 : 0;
      total += 100;
    }

    return total > 0 ? Math.round(score / total) : 0;
  };

  return {
    metrics,
    grade: getPerformanceGrade(),
  };
};

// === ACCESSIBILITY HELPERS ===

/**
 * Hook para gerenciar foco de acessibilidade
 */
export const useFocusManagement = () => {
  const focusRef = useRef<HTMLElement>(null);

  const focusElement = useCallback((element?: HTMLElement) => {
    const target = element || focusRef.current;
    if (target) {
      target.focus();
    }
  }, []);

  const trapFocus = useCallback((container: HTMLElement) => {
    const focusableElements = container.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    container.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return {
    focusRef,
    focusElement,
    trapFocus,
  };
};

/**
 * Hook para anúncios de screen reader
 */
export const useScreenReader = () => {
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;

    document.body.appendChild(announcement);

    setTimeout(() => {
      announcement.remove();
    }, 1000);
  }, []);

  return { announce };
};

// === UTILITÁRIOS DE OTIMIZAÇÃO ===

/**
 * Função para preload de rotas
 */
export const preloadRoute = (routePath: string) => {
  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.href = routePath;
  document.head.appendChild(link);
};

/**
 * Função para otimizar imagens
 */
export const optimizeImage = (
  src: string,
  width?: number,
  height?: number,
  quality: number = 85
): string => {
  if (!src) return '';

  // Se for uma URL externa, retornar como está
  if (src.startsWith('http')) return src;

  // Para imagens locais, adicionar parâmetros de otimização
  const params = new URLSearchParams();
  if (width) params.append('w', width.toString());
  if (height) params.append('h', height.toString());
  params.append('q', quality.toString());

  return `${src}?${params.toString()}`;
};

/**
 * Hook para detectar conexão lenta
 */
export const useNetworkStatus = () => {
  const [networkStatus, setNetworkStatus] = useState({
    online: navigator.onLine,
    connectionType: 'unknown',
    effectiveType: 'unknown',
    saveData: false,
  });

  useEffect(() => {
    const updateNetworkStatus = () => {
      const connection = (navigator as NetworkNavigator).connection || (navigator as NetworkNavigator).mozConnection || (navigator as NetworkNavigator).webkitConnection;

      setNetworkStatus({
        online: navigator.onLine,
        connectionType: connection?.type || 'unknown',
        effectiveType: connection?.effectiveType || 'unknown',
        saveData: connection?.saveData || false,
      });
    };

    updateNetworkStatus();

    window.addEventListener('online', updateNetworkStatus);
    window.addEventListener('offline', updateNetworkStatus);

    const connection = (navigator as NetworkNavigator).connection;
    if (connection) {
      connection.addEventListener('change', updateNetworkStatus);
    }

    return () => {
      window.removeEventListener('online', updateNetworkStatus);
      window.removeEventListener('offline', updateNetworkStatus);
      if (connection) {
        connection.removeEventListener('change', updateNetworkStatus);
      }
    };
  }, []);

  const isSlowConnection = networkStatus.effectiveType === 'slow-2g' || 
                          networkStatus.effectiveType === '2g' ||
                          networkStatus.saveData;

  return {
    ...networkStatus,
    isSlowConnection,
  };
};

// === CONSTANTS ===

export const PERFORMANCE_THRESHOLDS = {
  RENDER_TIME_WARNING: 100, // ms
  RENDER_TIME_ERROR: 300, // ms
  CACHE_TTL_DEFAULT: 300000, // 5 minutes
  DEBOUNCE_DELAY_DEFAULT: 300, // ms
  THROTTLE_DELAY_DEFAULT: 100, // ms
  LAZY_LOAD_THRESHOLD: 0.1,
  LAZY_LOAD_ROOT_MARGIN: '50px',
} as const;

export const ACCESSIBILITY_CONSTANTS = {
  FOCUS_TRAP_SELECTOR: 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
  SCREEN_READER_DELAY: 1000, // ms
} as const;
